import type { AgentAnnotationArtifact } from 'features/ask-yogi/types'
import { CHARTS_2_URL } from 'features/charts-v2/paths'
import {
  buildSocialTopicPath,
  SOCIAL_TOPICS_ANNOTATION_FEATURE,
} from 'features/coverage/features/social-topics/paths'
import { DASHBOARD_URL } from 'features/dashboard/paths'
import {
  DASHBOARDS_2_1_URL,
  dashboards21DetailPath,
} from 'features/dashboards-v2-1/paths'
import { buildProjectPageUrl } from 'routes/builders'
import { DASHBOARDS_2_URL } from 'src/features/dashboards-v2/paths'

type PathBuilder = (projectId: string, resourceId: string) => string

/**
 * Registry mapping feature URL route keys to path builder functions.
 * Add one entry per feature as annotation support is rolled out.
 *
 * The key must match the `feature` field on AgentAnnotationArtifact,
 * which in turn must match the nav routeNameMap key in ProjectLayout.tsx.
 *
 * Every dashboard key — v1 ('custom-dashboards'), v2 ('dashboards') and 2.1
 * ('dashboards-2-1') — opens Dashboards 2.1, whichever key the backend
 * annotation sends and whatever the hub's flags.
 *
 * A charts annotation (a saved view) carries the view's filter HASH as its
 * `resource_id`, not a uuid — the link opens the saved state directly.
 */
const DASHBOARD_FEATURES: ReadonlySet<string> = new Set([
  DASHBOARD_URL,
  DASHBOARDS_2_URL,
  DASHBOARDS_2_1_URL,
])

const FEATURE_PATH_BUILDERS: Record<string, PathBuilder> = {
  [DASHBOARD_URL]: (pid, rid) => dashboards21DetailPath(pid, rid),
  [DASHBOARDS_2_URL]: (pid, rid) => dashboards21DetailPath(pid, rid),
  [DASHBOARDS_2_1_URL]: (pid, rid) => dashboards21DetailPath(pid, rid),
  [CHARTS_2_URL]: (pid, rid) => buildProjectPageUrl(pid, CHARTS_2_URL, rid),
  [SOCIAL_TOPICS_ANNOTATION_FEATURE]: (pid, rid) =>
    buildSocialTopicPath(pid, rid),
}

/**
 * Returns true when the frontend knows how to navigate to this feature.
 * Unsupported features render as non-interactive text.
 */
export const isFeatureSupported = (feature: string): boolean =>
  feature in FEATURE_PATH_BUILDERS

/**
 * Builds the full navigation path for an annotation artifact.
 * Returns null for unknown feature keys so callers can degrade gracefully.
 *
 * A dashboard link carries `?cuid=<conversationId>` when one is given, so the
 * dashboard opens with the docked Ask Yogi panel on the conversation that made
 * it. A saved view's path is the view's own hash and takes no query.
 */
export const buildAnnotationPath = (
  projectId: string,
  ann: AgentAnnotationArtifact,
  conversationId?: string,
): string | null => {
  const path = FEATURE_PATH_BUILDERS[ann.feature]?.(projectId, ann.resource_id)
  if (!path) return null
  if (!conversationId || !DASHBOARD_FEATURES.has(ann.feature)) return path
  return `${path}?${new URLSearchParams({ cuid: conversationId })}`
}

const PROJECT_SEGMENT = /^\/project\/[^/]+/

const withoutProject = (path: string): string =>
  path
    .replace(/[?#].*$/, '')
    .replace(PROJECT_SEGMENT, '')
    .replace(/\/$/, '')

/**
 * Whether an annotation's path is the page at `pathname`. The project segment
 * and any query are ignored: the chat names the hub by its canonical uuid,
 * which need not be the string in the URL, and `?cuid` names the conversation,
 * not the page.
 */
export const isAnnotationForPage = (path: string, pathname: string): boolean =>
  withoutProject(path) === withoutProject(pathname)
